"use client";

import {
  Box,
  Button,
  Flex,
  HStack,
  Stack,
  Text,
  VisuallyHidden,
  chakra,
} from "@chakra-ui/react";
import { ReactNode, useMemo } from "react";
import { FaInstagram, FaYoutube } from "react-icons/fa";
import { useTranslations } from "next-intl";
import { FaLinkedinIn } from "react-icons/fa";
import { BsFacebook, BsSpotify, BsTwitterX } from "react-icons/bs";
import defaultLogo from "@root/public/images/logos/clarifayo_FundoPreto.svg";
import Image from "next/image";
import Link from "next/link";
import podcastLogo from "@root/public/images/logos/CoachTalks_Logo.svg";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { useRouter } from "next/router";
const ListHeader = ({ children }: { children: ReactNode }) => {
  return (
    <Text fontWeight={600} fontSize={"sm"} mb={2} color="brand.900">
      {children}
    </Text>
  );
};

const SocialButton = ({
  children,
  label,
  href,
}: {
  children: ReactNode;
  label: string;
  href: string;
}) => {
  return (
    <chakra.button
      rounded={"full"}
      cursor={"pointer"}
      as={"a"}
      href={href}
      display={"inline-flex"}
      alignItems={"center"}
      justifyContent={"center"}
      color="brand.100"
      fontSize="24px"
    >
      <VisuallyHidden>{label}</VisuallyHidden>
      {children}
    </chakra.button>
  );
};

export const Footer = () => {
  const { locale } = useRouter();
  const t = useTranslations();
  const { status } = useAuthenticatedUser();

  const MENU = useMemo(
    () => [
      {
        title: "CLARIFAYO.COM",
        links: [
          {
            label: t("contactUs"),
            href: `/${locale}/contact`,
            isButton: true,
          },
          {
            label: "FAQs",
            href: `/${locale}/faq`,
          },
          {
            label: t("CoacheePage.footerTitle"),
            href: `/${locale}/coachee`,
          },
          /* {
            label: t("lives.title"),
            href: "/terms",
          },
          {
            label: t("courses"),
            href: "/privacy",
          }, */
        ],
      },
    ],
    [t, locale]
  );

  return (
    <Box w="full" bgColor="brand.100">
      <Box
        bg={"brand.1000"}
        color={"white"}
        p={{
          base: 8,
          md: 20,
        }}
        w="full"
      >
        <Stack
          direction={["column", "column", "row"]}
          gap={32}
          w="full"
          justifyContent="space-between"
        >
          <Box flex={1}>
            <Image src={defaultLogo} alt="logo" width={200} height={200} />
          </Box>
          <Stack spacing={24} mt={15} direction={["column", "row"]} flex={1}>
            <Box flex={1}>
              <Stack spacing={2}>
                {MENU[0].links.map((link, index) => (
                  <Box key={index} textAlign="right">
                    {link.isButton ? (
                      <Button
                        as="a"
                        href={link.href}
                        bg="brand.900"
                        color="neutral.900"
                        p={2}
                        px={3}
                        fontSize="sm"
                        fontWeight="light"
                        borderRadius="3xl"
                        textTransform="uppercase"
                        _hover={{ bg: "brand.700" }}
                        w="auto"
                      >
                        {link.label}
                      </Button>
                    ) : (
                      <Text
                        fontSize="md"
                        p={1}
                        rounded={"3xl"}
                        textAlign="right"
                        color={index === 0 ? "black" : "neutral.0"}
                        bg={index === 0 ? "brand.900" : "transparent"}
                        _hover={{
                          color: "brand.900",
                          bg: "transparent",
                        }}
                      >
                        <Link href={link.href}>{link.label}</Link>
                      </Text>
                    )}
                  </Box>
                ))}

                <Link
                  href={
                    "https://open.spotify.com/show/4neoAl1SyfVLAsMgGJqpkf?si=40ffdb00536d4357"
                  }
                  target="_blank"
                >
                  <HStack gap={4} justifyContent="flex-end">
                    <Text>Podcast:</Text>{" "}
                    <Image
                      src={podcastLogo}
                      alt="Podcast"
                      width={100}
                      height={50}
                    />
                  </HStack>
                </Link>
              </Stack>
            </Box>

            <Box flex={1}>
              <Stack spacing={4} mt={4}>
                <Text
                  fontSize="md"
                  color="neutral.0"
                  textTransform="uppercase"
                  textAlign="right"
                  _hover={{
                    color: "brand.900",
                  }}
                >
                  <Link href="/to-whom">{t("landingPage.toWhom")}</Link>
                </Text>
                <Text
                  fontSize="md"
                  color="neutral.0"
                  textTransform="uppercase"
                  textAlign="right"
                  _hover={{
                    color: "brand.900",
                  }}
                >
                  <Link href="/pricing">{t("pricing")}</Link>
                </Text>
                <Text
                  fontSize="md"
                  color="neutral.0"
                  textTransform="uppercase"
                  textAlign="right"
                  _hover={{
                    color: "brand.900",
                  }}
                >
                  <Link href="/learning">{t("learning")}</Link>
                </Text>
                <Text
                  fontSize="md"
                  color="neutral.0"
                  textTransform="uppercase"
                  textAlign="right"
                  _hover={{
                    color: "brand.900",
                  }}
                >
                  <Link href="/coach-community">{t("coachCommunity")}</Link>
                </Text>
                <Text
                  fontSize="md"
                  color="neutral.0"
                  textTransform="uppercase"
                  textAlign="right"
                  _hover={{
                    color: "brand.900",
                  }}
                >
                  <Link
                    href={
                      status === "authenticated" ? "/router" : "/auth/sign-in"
                    }
                  >
                    {t("startSession")}
                  </Link>
                </Text>
              </Stack>
            </Box>
          </Stack>
        </Stack>
        <Stack direction={["column", "column", "row"]} gap={32} w="full">
          <Box>
            <Box mt={10}>
              <Text fontSize="sm" fontWeight="medium">
                {t("followUs")}
              </Text>
              <Stack direction={"row"} spacing={8}>
                <SocialButton
                  label={"Instagram"}
                  href={"https://www.instagram.com/clarifayo"}
                >
                  <FaInstagram fontSize="30px" />
                </SocialButton>
                <SocialButton label={"YouTube"} href={"#"}>
                  <FaYoutube fontSize="30px" />
                </SocialButton>
                <SocialButton
                  label={"LinkedIn"}
                  href={"https://www.linkedin.com/company/clarifayo/"}
                >
                  <FaLinkedinIn fontSize="40px" />
                </SocialButton>
                <SocialButton label={"Facebook"} href={"#"}>
                  <BsFacebook fontSize="30px" />
                </SocialButton>
                <SocialButton label={"Spotify"} href={"#"}>
                  <BsSpotify fontSize="30px" />
                </SocialButton>
              </Stack>
            </Box>
          </Box>
        </Stack>
        <Stack
          w="full"
          direction={["column", "row"]}
          mt={10}
          justifyContent={"space-between"}
        >
          <Text fontSize="sm" color="neutral.0">
            <Text as="span" color="brand.900">
              &copy; {new Date().getFullYear()} Clarifayo.
            </Text>{" "}
            {t("copyright")}
          </Text>
          <Stack
            flex={1}
            direction={["column", "row"]}
            spacing={4}
            w="full"
            justifyContent={"flex-end"}
          >
            <Text
              fontSize="sm"
              color="neutral.0"
              textAlign="right"
              _hover={{
                color: "brand.900",
              }}
            >
              <Link href="/privacy">{t("privacyAndCookiePolicy")}</Link>
            </Text>
            <Text
              fontSize="sm"
              color="neutral.0"
              textAlign="right"
              _hover={{
                color: "brand.900",
              }}
            >
              <Link href="/terms">{t("termsOfService")}</Link>
            </Text>
            <Text
              fontSize="sm"
              color="neutral.0"
              textAlign="right"
              _hover={{
                color: "brand.900",
              }}
            >
              <Link href="/code-of-conduct">{t("codeOfConduct")}</Link>
            </Text>
          </Stack>
        </Stack>
      </Box>
    </Box>
  );
};
